import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import type { ProposalAPIError } from '@/lib/proposals/core/errors';
import { useReopenProposal } from '@/lib/proposals/query';

import { ActionError } from './shared/ActionError';

interface ReopenControlProps {
  orgId: number;
  proposalId: number;
}

export function ReopenControl({ orgId, proposalId }: ReopenControlProps) {
  const [error, setError] = useState<ProposalAPIError | null>(null);
  // onError is passed at the hook level, NOT to mutate(): the success cache
  // update flips the proposal to pending, which unmounts this control (it only
  // renders for rejected proposals) — and TanStack skips mutate()-level
  // callbacks once the component is unmounted. Hook-level options always run.
  // No onSuccess is needed: the list tab follows the viewed action's
  // resolution (see ProposalCenter), so it flips to Open automatically.
  const mutation = useReopenProposal(orgId, {
    onError: (e: ProposalAPIError) => setError(e),
  });

  const handleReopen = useCallback(() => {
    setError(null);
    mutation.mutate({ proposalId });
  }, [mutation, proposalId]);

  return (
    <div className='border-t border-primary bg-primary pb-[env(safe-area-inset-bottom)]'>
      <div className='px-3 sm:px-4 lg:px-6 py-3 flex flex-col gap-3'>
        {error && (error.isNetworkError() || error.isServerError()) && (
          <ActionError
            message="We couldn't reach the server to reopen this action. Please try again."
            onRetry={handleReopen}
          />
        )}
        {error && !error.isNetworkError() && !error.isServerError() && (
          <ActionError message='Something went wrong. Try again, and get in touch if it keeps happening.' />
        )}
        <div className='flex justify-end'>
          <Button
            size='md'
            isLoading={mutation.isPending}
            onClick={handleReopen}
          >
            Reopen
          </Button>
        </div>
      </div>
    </div>
  );
}
